/**
 * SAT Navigator - pushState for forward navigation, popstate for browser back.
 *
 * Screens that represent a user decision get a history entry. Transient
 * loaders are shown with showScreen(), which deliberately leaves history
 * alone so back never lands the user inside work that already finished.
 */
(function (global) {
  "use strict";

  var screens = {};
  var isCaptureOverlay = false;
  var currentScreen = null;

  function setScreenVisibility(activeScreen) {
    Object.keys(screens).forEach(function (name) {
      var def = screens[name];
      if (!def || !def.el) return;
      var active = name === activeScreen;
      def.el.classList.toggle("sat-screen--active", active);
      def.el.setAttribute("aria-hidden", active ? "false" : "true");
    });
  }

  function renderScreen(screen, prevScreen) {
    var prev = prevScreen !== undefined ? prevScreen : currentScreen;
    currentScreen = screen;

    // The pre-paint gate in sat-ai-shell deliberately outranks
    // .sat-screen--active, so it has to go the moment we render anything -
    // otherwise it pins its screen open for the rest of the page's life.
    // Clearing it here rather than at the call site means no failure elsewhere
    // can leave the page stuck on the gated screen.
    global.document.documentElement.removeAttribute("data-sat-initial-screen");

    setScreenVisibility(screen);

    var def = screens[screen];
    if (def && typeof def.onEnter === "function") {
      def.onEnter(screen, prev);
    }
  }

  /**
   * Unknown names would hide every screen and leave a blank page, so anything
   * unregistered resolves to landing.
   */
  function knownScreen(name) {
    return name && Object.prototype.hasOwnProperty.call(screens, name)
      ? name
      : "landing";
  }

  function urlFor(screen) {
    var url = global.location.pathname;
    if (screen !== "landing") {
      url += "?screen=" + screen;
    }
    return url;
  }

  global.SATNavigator = {
    registerScreens: function (registry) {
      screens = registry;
    },

    init: function () {
      var initialScreen = this.screenFromUrl();

      // Keep state and rendered screen in agreement, and preserve the URL so a
      // refresh on ?screen=review comes back to the same place.
      global.history.replaceState(
        { satScreen: initialScreen },
        "",
        urlFor(initialScreen),
      );

      renderScreen(initialScreen, null);

      global.addEventListener("popstate", function (e) {
        if (isCaptureOverlay) {
          isCaptureOverlay = false;
          if (global.SATIframeBridge) {
            global.SATIframeBridge.close();
          }
        }

        var screen = knownScreen(e.state && e.state.satScreen);
        renderScreen(screen);
      });
    },

    getCurrentScreen: function () {
      return currentScreen;
    },

    /**
     * The screen the URL says we are on. Unlike getCurrentScreen() this never
     * reports a transient loader, because showScreen() leaves the URL alone -
     * which makes it the right source of truth after a bfcache restore, where
     * the in-memory screen is whatever was frozen mid-flight.
     */
    screenFromUrl: function () {
      var params = new URLSearchParams(global.location.search);
      return knownScreen(params.get("screen"));
    },

    /** Navigate to a screen, adding a history entry. */
    goTo: function (screen) {
      global.history.pushState({ satScreen: screen }, "", urlFor(screen));
      renderScreen(screen);
    },

    /** Show a screen without touching history - for loaders. */
    showScreen: function (screen) {
      renderScreen(screen);
    },

    goToPage: function (page) {
      var urls = {
        analysis: "/pages/sat-ai-analysis",
        results: "/pages/sat-ai-results",
        landing: "/pages/sat-ai",
      };
      var url = urls[page];
      if (url) {
        global.location.href = url;
      }
    },

    openOverlay: function (forceReload) {
      isCaptureOverlay = true;
      if (global.SATIframeBridge) {
        global.SATIframeBridge.open(forceReload);
      }
    },

    closeOverlay: function () {
      isCaptureOverlay = false;
      if (global.SATIframeBridge) {
        global.SATIframeBridge.close();
      }
    },

    isCaptureOverlay: function () {
      return isCaptureOverlay;
    },

    back: function () {
      global.history.back();
    },
  };
})(window);
